// Hook to compute byte length (EUC-KR heuristic) and classify SMS vs LMS.
// Heuristic: GSM 7-bit basic chars count as 1 byte, others (including Korean) count as 2.
// Thresholds (common KR carrier standards): SMS <= 90 bytes, LMS <= 2000 bytes.
import { useMemo } from 'react';

export interface MessageLengthInfo {
  bytes: number;
  isSms: boolean;
  isLms: boolean;
  overLimit: boolean;
  classification: 'SM' | 'LM' | 'OVER';
}

const GSM_7BIT_REGEX = /^[A-Za-z0-9 @£$!"#%&'()*+,\-./:;<=>?\n\r\u000c\u000b]*$/; // simplified

export function computeMessageBytes(message: string): number {
  if (!message) return 0;
  let bytes = 0;
  for (let i = 0; i < message.length; i++) {
    const ch = message[i];
    if (GSM_7BIT_REGEX.test(ch)) bytes += 1; else bytes += 2;
  }
  return bytes;
}

export function classifyMessage(message: string): 'SM' | 'LM' | 'OVER' {
  const bytes = computeMessageBytes(message);
  if (bytes <= 90) return 'SM';
  if (bytes <= 2000) return 'LM';
  return 'OVER';
}

export function useMessageLength(message: string): MessageLengthInfo {
  return useMemo(() => {
    const bytes = computeMessageBytes(message || '');
    const classification = classifyMessage(message || '');
    return {
      bytes,
      isSms: classification === 'SM',
      isLms: classification === 'LM',
      overLimit: classification === 'OVER',
      classification
    };
  }, [message]);
}

export default useMessageLength;
